/**
 * Door Staff Management Page
 * Dedicated page for managing door staff assignments for an event
 * Accessible at /app/events/{event_id}/door-staff
 */

import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { usePermissions } from '@/hooks/usePermissions';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { PageHeader, EmptyState, BadgePill } from '@/components/campus';
import {
  ShieldCheck,
  Users,
  UserPlus,
  UserMinus,
  Loader2,
  ArrowLeft,
  Calendar,
  MapPin,
  Clock,
  Search,
  CheckCircle,
  XCircle,
} from 'lucide-react';
import { cn } from '@/lib/utils';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Format date helper
const formatDate = (dateStr) => {
  if (!dateStr) return '';
  return new Date(dateStr).toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
  });
};

const formatTime = (dateStr) => {
  if (!dateStr) return '';
  return new Date(dateStr).toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
  });
};

// ==================== STAFF CARD COMPONENT ====================
const StaffCard = ({ staff, isAssigned, onAssign, onRemove, loading }) => (
  <div
    className={cn(
      "flex items-center justify-between p-4 rounded-xl border transition-colors",
      isAssigned
        ? "bg-cool-mint/5 border-cool-mint/20"
        : "bg-graphite/50 border-white/[0.06] hover:border-white/[0.1]"
    )}
    data-testid={`staff-card-${staff.user_id}`}
  >
    <div className="flex items-center gap-4">
      {/* Avatar */}
      {staff.avatar_url ? (
        <img
          src={staff.avatar_url}
          alt={staff.display_name}
          className="w-12 h-12 rounded-full object-cover border-2 border-white/[0.1]"
        />
      ) : (
        <div className="w-12 h-12 rounded-full bg-electric-ice-blue/20 flex items-center justify-center">
          <Users className="w-5 h-5 text-electric-ice-blue" />
        </div>
      )}

      {/* Info */}
      <div>
        <div className="flex items-center gap-2">
          <p className="font-medium text-cloud-white">{staff.display_name}</p>
          {isAssigned && (
            <BadgePill variant="mint" size="sm">
              <CheckCircle className="w-3 h-3 mr-1" />
              Assigned
            </BadgePill>
          )}
        </div>
        <p className="text-sm text-soft-silver">{staff.email}</p>
      </div>
    </div>

    {/* Action Button */}
    {isAssigned ? (
      <Button
        variant="outline"
        size="sm"
        onClick={() => onRemove(staff)}
        disabled={loading}
        className="border-signal-red/30 text-signal-red hover:bg-signal-red/10"
        data-testid={`remove-staff-${staff.user_id}`}
      >
        {loading ? (
          <Loader2 className="w-4 h-4 animate-spin" />
        ) : (
          <>
            <UserMinus className="w-4 h-4 mr-2" />
            Remove
          </>
        )}
      </Button>
    ) : (
      <Button
        size="sm"
        onClick={() => onAssign(staff)}
        disabled={loading}
        className="bg-electric-ice-blue text-white hover:bg-electric-ice-blue/90"
        data-testid={`assign-staff-${staff.user_id}`}
      >
        {loading ? (
          <Loader2 className="w-4 h-4 animate-spin" />
        ) : (
          <>
            <UserPlus className="w-4 h-4 mr-2" />
            Assign
          </>
        )}
      </Button>
    )}
  </div>
);

// ==================== MAIN PAGE COMPONENT ====================
export const DoorStaffManagement = () => {
  const { eventId } = useParams();
  const navigate = useNavigate();
  const { organization, session } = useAuth();
  const { hasOrgRole } = usePermissions();

  // Permission check
  const canManage = hasOrgRole(['owner', 'admin']);

  // State
  const [event, setEvent] = useState(null);
  const [allDoorStaff, setAllDoorStaff] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');

  // Fetch event and door staff data
  const fetchData = useCallback(async () => {
    if (!eventId || !organization?.id || !session?.access_token) {
      setLoading(false);
      return;
    }

    setLoading(true);

    try {
      // Fetch event details
      const eventRes = await fetch(
        `${API_URL}/api/events/${eventId}`,
        { headers: { Authorization: `Bearer ${session.access_token}` } }
      );

      if (eventRes.ok) {
        const eventData = await eventRes.json();
        setEvent(eventData);
      }

      // Fetch all door staff users in org
      const staffRes = await fetch(
        `${API_URL}/api/door-staff/organization/${organization.id}/door-staff-users`,
        { headers: { Authorization: `Bearer ${session.access_token}` } }
      );

      if (staffRes.ok) {
        const staffData = await staffRes.json();
        setAllDoorStaff(staffData.door_staff || []);
      }

      // Fetch current assignments for this event
      const assignRes = await fetch(
        `${API_URL}/api/door-staff/event/${eventId}/assignments`,
        { headers: { Authorization: `Bearer ${session.access_token}` } }
      );

      if (assignRes.ok) {
        const assignData = await assignRes.json();
        setAssignments(assignData.assignments || []);
      }
    } catch (error) {
      console.error('Failed to fetch door staff data:', error);
      toast.error('Failed to load door staff data');
    } finally {
      setLoading(false);
    }
  }, [eventId, organization?.id, session?.access_token]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Check if a user is assigned
  const isUserAssigned = (userId) => {
    return assignments.some((a) => a.user_id === userId);
  };

  // Get assignment for a user
  const getAssignmentForUser = (userId) => {
    return assignments.find((a) => a.user_id === userId);
  };

  // Handle assign
  const handleAssign = async (staff) => {
    setActionLoading(staff.user_id);

    try {
      const res = await fetch(`${API_URL}/api/door-staff/assign`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({
          user_id: staff.user_id,
          event_id: eventId,
        }),
      });

      const data = await res.json();

      if (data.success) {
        toast.success(`${staff.display_name} assigned successfully`);
        // Refresh assignments
        fetchData();
      } else {
        toast.error(data.message || 'Failed to assign');
      }
    } catch (error) {
      console.error('Assign error:', error);
      toast.error('Failed to assign door staff');
    } finally {
      setActionLoading(null);
    }
  };

  // Handle remove
  const handleRemove = async (staff) => {
    const assignment = getAssignmentForUser(staff.user_id);
    if (!assignment) return;

    setActionLoading(staff.user_id);

    try {
      const res = await fetch(
        `${API_URL}/api/door-staff/assignment/${assignment.id}`,
        {
          method: 'DELETE',
          headers: { Authorization: `Bearer ${session.access_token}` },
        }
      );

      if (res.ok) {
        toast.success(`${staff.display_name} removed from event`);
        setAssignments((prev) => prev.filter((a) => a.id !== assignment.id));
      } else {
        toast.error('Failed to remove');
      }
    } catch (error) {
      console.error('Remove error:', error);
      toast.error('Failed to remove assignment');
    } finally {
      setActionLoading(null);
    }
  };

  // Filter staff by search
  const filteredStaff = allDoorStaff.filter((staff) =>
    staff.display_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    staff.email?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Separate assigned and unassigned
  const assignedStaff = filteredStaff.filter((s) => isUserAssigned(s.user_id));
  const unassignedStaff = filteredStaff.filter((s) => !isUserAssigned(s.user_id));

  // Access denied
  if (!canManage) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <EmptyState
          icon="lock"
          title="Access Restricted"
          description="Only owners and admins can manage door staff assignments."
        />
      </div>
    );
  }

  // Loading
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="w-8 h-8 text-electric-ice-blue animate-spin" />
          <p className="text-soft-silver text-sm">Loading door staff...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="door-staff-management-page">
      {/* Back Button */}
      <Button
        variant="ghost"
        onClick={() => navigate(-1)}
        className="text-soft-silver hover:text-cloud-white -ml-2"
      >
        <ArrowLeft className="w-4 h-4 mr-2" />
        Back to Events
      </Button>

      <PageHeader
        title="Door Staff Assignment"
        subtitle={event?.event_name || 'Loading...'}
        icon={ShieldCheck}
      />

      {/* Event Info Card */}
      {event && (
        <div className="p-5 bg-graphite/50 rounded-xl border border-white/[0.06]">
          <div className="flex flex-wrap gap-6">
            <div className="flex items-center gap-2 text-soft-silver">
              <Calendar className="w-4 h-4 text-electric-ice-blue" />
              <span>{formatDate(event.start_time)}</span>
            </div>
            <div className="flex items-center gap-2 text-soft-silver">
              <Clock className="w-4 h-4 text-electric-ice-blue" />
              <span>{formatTime(event.start_time)} - {formatTime(event.end_time)}</span>
            </div>
            <div className="flex items-center gap-2 text-soft-silver">
              <MapPin className="w-4 h-4 text-electric-ice-blue" />
              <span>{event.location}</span>
            </div>
          </div>
        </div>
      )}

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-text" />
        <Input
          placeholder="Search door staff..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="pl-10 bg-graphite border-white/[0.08] text-cloud-white"
        />
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 gap-4">
        <div className="p-4 bg-cool-mint/10 rounded-xl border border-cool-mint/20">
          <p className="text-2xl font-mono font-bold text-cool-mint">{assignments.length}</p>
          <p className="text-sm text-soft-silver">Assigned Staff</p>
        </div>
        <div className="p-4 bg-graphite/50 rounded-xl border border-white/[0.06]">
          <p className="text-2xl font-mono font-bold text-cloud-white">{allDoorStaff.length}</p>
          <p className="text-sm text-soft-silver">Total Door Staff</p>
        </div>
      </div>

      {/* Currently Assigned Section */}
      {assignedStaff.length > 0 && (
        <div>
          <h3 className="text-sm font-medium text-soft-silver mb-3 flex items-center gap-2">
            <CheckCircle className="w-4 h-4 text-cool-mint" />
            Currently Assigned ({assignedStaff.length})
          </h3>
          <div className="space-y-3">
            {assignedStaff.map((staff) => (
              <StaffCard
                key={staff.user_id}
                staff={staff}
                isAssigned={true}
                onRemove={handleRemove}
                loading={actionLoading === staff.user_id}
              />
            ))}
          </div>
        </div>
      )}

      {/* Available Staff Section */}
      <div>
        <h3 className="text-sm font-medium text-soft-silver mb-3 flex items-center gap-2">
          <Users className="w-4 h-4" />
          Available Door Staff ({unassignedStaff.length})
        </h3>

        {allDoorStaff.length === 0 ? (
          <div className="text-center py-12 bg-graphite/30 rounded-xl border border-white/[0.06]">
            <Users className="w-12 h-12 text-muted-text mx-auto mb-3" />
            <p className="text-soft-silver">No users with door_staff role found</p>
            <p className="text-sm text-muted-text mt-1">
              Assign the door_staff role to members in the Members page first
            </p>
          </div>
        ) : unassignedStaff.length === 0 ? (
          <div className="text-center py-8 bg-graphite/30 rounded-xl border border-white/[0.06]">
            <CheckCircle className="w-10 h-10 text-cool-mint mx-auto mb-3" />
            <p className="text-soft-silver">All door staff have been assigned</p>
          </div>
        ) : (
          <div className="space-y-3">
            {unassignedStaff.map((staff) => (
              <StaffCard
                key={staff.user_id}
                staff={staff}
                isAssigned={false}
                onAssign={handleAssign}
                loading={actionLoading === staff.user_id}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default DoorStaffManagement;
